import LockIcon from '@mui/icons-material/Lock';
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from '@mui/material';
import { FormEvent, ReactNode, useState } from 'react';
import { Title } from 'react-admin';
import { devToolsPassword, isConfigured, isUnlocked, unlock } from '../DevTools/devToolsAuth';

interface TemplatesGateProps {
  children: ReactNode;
  /** Injectable for tests; defaults to the Dev Tools password. */
  password?: string;
}

/**
 * Templates change what pay policies compute, so the page asks for the Dev Tools password first:
 * a speed bump against changes by accident, not access control. One unlock opens both for the session.
 */
const TemplatesGate = ({ children, password = devToolsPassword() }: TemplatesGateProps) => {
  const [unlocked, setUnlocked] = useState(() => isUnlocked());
  const [attempt, setAttempt] = useState('');
  const [failed, setFailed] = useState(false);

  if (unlocked) return <>{children}</>;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (unlock(attempt, password)) setUnlocked(true);
    else setFailed(true);
  };

  return (
    <Card sx={{ maxWidth: 420, mt: 4, mx: 'auto' }}>
      <Title title="Pay-Policy Templates" />
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LockIcon />
          <Typography variant="h6">Pay-Policy Templates</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Templates change what pay policies compute. Enter the Dev Tools password to open them.
        </Typography>
        {!isConfigured(password) ? (
          <Alert severity="warning">The Dev Tools password is not configured for this environment.</Alert>
        ) : (
          <form onSubmit={submit}>
            <TextField label="Password" type="password" fullWidth autoFocus value={attempt} onChange={(event) => setAttempt(event.target.value)} error={failed} helperText={failed ? 'Incorrect password' : ' '} />
            <Button type="submit" variant="contained" sx={{ mt: 1 }}>
              Unlock
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default TemplatesGate;

/** The list and the editor, each behind the gate. */
export const withTemplatesGate = (Page: () => JSX.Element) => {
  const Gated = () => (
    <TemplatesGate>
      <Page />
    </TemplatesGate>
  );
  return Gated;
};
